import { formatDate, formatDateOrDash } from '@/lib/format/date';
import { formatTextOrDash } from '@/lib/format/text';
import { formatNumber, formatPercentOrDash } from '@/lib/format/number';
import {
  buildQuantitiesByUnit,
  buildRawQuantitiesByUnit,
  defaultUnitType,
} from '@/lib/mappers/quantities';
import { UNIT_TYPES } from '@/config/unitTypes.constants';
import { buildOutgoingShipmentFiltersInput, processFilterOptions } from '@/lib/mappers/filters';
import {
  FACILITY_KINDS,
  buildFacilityValue,
  parseFacilityValue,
  allocationPreferenceLabel,
  feedstockCategoryLabel,
  toAllocationPreferenceArray,
} from '@/config/formOptions';
import { allocationStatusLabel, orderDisplayStatusLabel } from '@/config/statuses';
import { outgoingShipmentStatusLabel } from '../constants/outgoingShipmentStatus';
import {
  OUTGOING_SHIPMENT_SORT_FIELDS,
  SHIPMENT_LINKED_ORDER_SORT_FIELDS,
} from '../constants/outgoingShipments.constants';

// DTO ⇄ view model: the `outgoingShipments` GraphQL query for the list, REST for the writes.

// The server ORs OutgoingShipmentSearch's keys, so one box searches all four.
const buildOutgoingShipmentSearchInput = (search) => {
  const term = search?.trim();
  if (!term) return undefined;
  const text = { contains: term, mode: 'insensitive' };
  return {
    shipmentNo: text,
    orderNo: text,
    customerAccount: { companyname: text },
    material: { name: text },
  };
};

export const buildOutgoingShipmentsQuery = ({
  certificateType,
  page,
  pageSize,
  sortBy,
  sortDir,
  search,
  materialId,
  customerAccountId,
  extraFilters,
} = {}) => {
  const sortField = OUTGOING_SHIPMENT_SORT_FIELDS[sortBy];
  return {
    certificateType,
    page,
    limit: pageSize,
    // An unsupported sort field would fail the query.
    orderBy: sortField ? [{ field: sortField, direction: sortDir ?? 'asc' }] : undefined,
    search: buildOutgoingShipmentSearchInput(search),
    filters: buildOutgoingShipmentFiltersInput({ materialId, customerAccountId }, extraFilters),
  };
};

export const processOutgoingShipmentFilterOptions = (result) => ({
  materialOptions: processFilterOptions(result?.materials),
  customerOptions: processFilterOptions(result?.customers),
});

// The server returns each quantity twice, once per unit, so the row can switch.
const buildUnits = (node) => ({
  [UNIT_TYPES.working]: {
    unit: node.workingUnit,
    amounts: {
      unassignedQuantity: node.unassignedQuantityWorking,
      shipmentQuantity: node.totalQuantityWorking,
      allocatedQuantity: node.allocatedQuantityWorking,
      remainingToAllocateQuantity: node.remainingToAllocateQuantityWorking,
    },
  },
  [UNIT_TYPES.declaration]: {
    unit: node.declarationUnit,
    amounts: {
      unassignedQuantity: node.unassignedQuantityDeclaration,
      shipmentQuantity: node.totalQuantityDeclaration,
      allocatedQuantity: node.allocatedQuantityDeclaration,
      remainingToAllocateQuantity: node.remainingToAllocateQuantityDeclaration,
    },
  },
});

// One table row, display-ready.
export const processOutgoingShipment = (node) => {
  const unitsByType = buildUnits(node);
  const byUnit = buildQuantitiesByUnit(unitsByType);
  return {
    id: node.id,
    shipmentId: formatTextOrDash(node.shipmentNo),
    materialName: formatTextOrDash(node.material?.name),
    linkedOrders: node.linkedOrders?.length ?? 0,
    facilityName: formatTextOrDash(node.site?.siteName ?? node.backToBackAccount?.accountName),
    // `status` is the derived one; `shipmentStatus` is the raw enum behind it.
    status: outgoingShipmentStatusLabel(node.status ?? node.shipmentStatus) ?? '',
    soldTo: formatTextOrDash(node.customerAccount?.companyname),
    terminal: formatTextOrDash(node.terminal),
    vesselName: formatTextOrDash(node.vesselName),
    // `shippingFrom` holds the site name — see processShippingDetail in lib/mappers/lookups.
    shippingTo: formatTextOrDash(
      node.shippingDetails?.shippingFrom ?? node.shippingDetails?.shippingTo,
    ),
    byUnit,
    // The link wizard deducts what step 2 assigns from unassigned, which needs the number.
    rawByUnit: buildRawQuantitiesByUnit(unitsByType),
    unitType: defaultUnitType(byUnit),
    materialId: node.material?.id ?? null,
    siteId: node.site?.id ?? null,
    customerAccountId: node.customerAccount?.id ?? null,
    // Flat numbers, the shape AssignQuantityCell caps against in the bulk wizard.
    unassignedByUnit: {
      [UNIT_TYPES.working]: node.unassignedQuantityWorking ?? null,
      [UNIT_TYPES.declaration]: node.unassignedQuantityDeclaration ?? null,
    },
    // Raw, not `byUnit`'s string, and either unit: a working-only material sends no declaration.
    hasAllocations:
      Number(node.allocatedQuantityDeclaration ?? 0) > 0 ||
      Number(node.allocatedQuantityWorking ?? 0) > 0,
    dateOfDispatch: formatDateOrDash(node.dispatchDate),
    discrepancyCount: node.discrepancyCount ?? 0,
  };
};

// The detail header + "Outgoing shipment details" tab — same node type as a row, so `buildUnits` carries the three quantities the tab renders.
export const processOutgoingShipmentDetail = (node) => {
  if (!node) return null;
  const unitsByType = buildUnits(node);
  const byUnit = buildQuantitiesByUnit(unitsByType);
  return {
    id: node.id,
    shipmentId: formatTextOrDash(node.shipmentNo),
    materialName: formatTextOrDash(node.material?.name),
    facilityName: formatTextOrDash(node.site?.siteName ?? node.backToBackAccount?.accountName),
    terminal: formatTextOrDash(node.terminal),
    vesselName: formatTextOrDash(node.vesselName),
    // The derived status drives the badge tone, so the raw code is kept beside its label.
    statusCode: node.status ?? node.shipmentStatus ?? null,
    statusLabel: formatTextOrDash(outgoingShipmentStatusLabel(node.status ?? node.shipmentStatus)),
    soldTo: formatTextOrDash(node.customerAccount?.companyname),
    shippingTo: formatTextOrDash(
      node.shippingDetails?.shippingFrom ?? node.shippingDetails?.shippingTo,
    ),
    dateOfDispatch: formatDateOrDash(node.dispatchDate),
    byUnit,
    // Linked orders' Assigned tile is shipment − unassigned, which needs the numbers.
    rawByUnit: buildRawQuantitiesByUnit(unitsByType),
    // The ids the Link-orders candidate filter needs — the save route matches on these three.
    materialId: node.material?.id ?? null,
    siteId: node.site?.id ?? null,
    customerAccountId: node.customerAccount?.id ?? null,
    // Raw: the wizard is unavailable at 0 and checks its running total against this cap.
    unassignedByUnit: {
      [UNIT_TYPES.working]: node.unassignedQuantityWorking ?? null,
      [UNIT_TYPES.declaration]: node.unassignedQuantityDeclaration ?? null,
    },
    // Raw, not `byUnit`'s string, and either unit: a working-only material sends no declaration.
    hasAllocations:
      Number(node.allocatedQuantityDeclaration ?? 0) > 0 ||
      Number(node.allocatedQuantityWorking ?? 0) > 0,
    discrepancyCount: node.discrepancyCount ?? 0,
    // Allocation details tab tile; "-" until something is fulfilled.
    allocatedGhgSavings: formatPercentOrDash(node.avgGhgSaving),
  };
};

// An `OutgoingShipmentConnection` → rows + totalCount; `total` already respects search.
export const processOutgoingShipmentsPage = (connection) => ({
  rows: (connection?.nodes ?? []).map(processOutgoingShipment),
  totalCount: connection?.pageInfo?.total ?? 0,
});

// `assignedQuantity` is this order's share of the shipment; the rest are the order's own totals. Both sit under the SHIPMENT's units, which the server resolves onto the node itself.
const buildLinkedOrderByUnit = (node, order) =>
  buildQuantitiesByUnit({
    [UNIT_TYPES.working]: {
      unit: node.workingUnit,
      amounts: {
        assignedQuantity: node.assignedQuantityWorking,
        contractedQuantity: order.contractedWorkingQuantity,
        shippedQuantity: order.shippedWorkingQuantity,
        remainingToShipQuantity: order.remainingToShipWorkingQuantity,
        allocatedQuantity: order.allocatedWorkingQuantity,
        remainingToAllocateQuantity: order.remainingToAllocateWorkingQuantity,
      },
    },
    [UNIT_TYPES.declaration]: {
      unit: node.declarationUnit,
      amounts: {
        assignedQuantity: node.assignedQuantityDeclaration,
        contractedQuantity: order.contractedDeclarationQuantity,
        shippedQuantity: order.shippedDeclarationQuantity,
        remainingToShipQuantity: order.remainingToShipDeclarationQuantity,
        allocatedQuantity: order.allocatedDeclarationQuantity,
        remainingToAllocateQuantity: order.remainingToAllocateDeclarationQuantity,
      },
    },
  });

// Table's `{ id, dir }` → the tab's `orderBy`; an unmapped column would fail the query.
export const buildLinkedOrderOrderBy = (sort) => {
  const field = SHIPMENT_LINKED_ORDER_SORT_FIELDS[sort?.id];
  return field ? [{ field, direction: sort.dir ?? 'asc' }] : undefined;
};

// One row of the Linked orders tab — GraphQL LinkedOrdersForOutgoingShipment($shipmentId), one `ShipmentLinkedOrder` node per row.
export const processShipmentLinkedOrder = (node, index) => {
  const order = node.order ?? {};
  const byUnit = buildLinkedOrderByUnit(node, order);
  return {
    id: order.id,
    serialNo: index + 1,
    orderNo: formatTextOrDash(order.orderNo),
    status: orderDisplayStatusLabel(order.status) ?? '-',
    // What the Update-shipment modal carries over from the order, unformatted where it prefills a control.
    materialName: formatTextOrDash(order.material?.name),
    facilityName: formatTextOrDash(order.site?.siteName),
    soldTo: formatTextOrDash(order.customerAccount?.companyname),
    customerAccountId: order.customerAccount?.id ?? null,
    materialId: order.material?.id ?? null,
    siteId: order.site?.id ?? null,
    terminalName: order.terminal ?? '',
    terminalId: order.terminalRefId ?? null,
    shippingToId: order.shippingDetails?.id ?? null,
    dispatchDateInput: formatDate(order.dispatchDate, DATE_INPUT_FORMAT),
    byUnit,
    unitType: defaultUnitType(byUnit),
    tolerance: formatPercentOrDash(order.tolerance),
    maxGhgEmissions: order.ghgEmission == null ? '-' : formatNumber(order.ghgEmission),
    minGhgSavings: formatPercentOrDash(order.ghgSaving),
    // Delivered, not targeted — null until something on this order is fulfilled.
    allocatedGhgSavings: formatPercentOrDash(order.avgGhgSaving),
    rawMaterialType: formatTextOrDash(order.rawMaterialType),
    countryOfOrigin: formatTextOrDash(order.countryOfOrigin),
    feedstockCategory: formatTextOrDash(feedstockCategoryLabel(order.feedstockCategory)),
    // preferredAllocation is a single scalar, not a list — never assume an array.
    allocationPreference: formatTextOrDash(
      toAllocationPreferenceArray(order.preferredAllocation)
        .map(allocationPreferenceLabel)
        .join(', '),
    ),
    dispatchBy: formatDateOrDash(order.dispatchDate),
  };
};

const buildShipmentAllocationByUnit = (dto) =>
  buildQuantitiesByUnit({
    [UNIT_TYPES.working]: {
      unit: dto.workingUnit,
      amounts: { certifiedQuantity: dto.certifiedQuantityWorking },
    },
    [UNIT_TYPES.declaration]: {
      unit: dto.declarationUnit,
      amounts: { certifiedQuantity: dto.certifiedQuantityDeclaration },
    },
  });

// One row of the Allocation details tab — GraphQL allocationDetailsForOutgoingShipment, one `Fulfillment` node per row.
const processShipmentAllocation = (dto) => {
  const byUnit = buildShipmentAllocationByUnit(dto);
  return {
    id: dto.id,
    transactionId: formatTextOrDash(dto.transactionId),
    status: allocationStatusLabel(dto.approvalStatus) ?? formatTextOrDash(dto.approvalStatus),
    statusCode: dto.approvalStatus ?? null,
    facilityName: formatTextOrDash(dto.sentFrom),
    sendTo: formatTextOrDash(dto.receivedAt),
    shippingTo: formatTextOrDash(dto.shippingTo),
    shippingToSites: formatTextOrDash(dto.shippingToSites),
    transactionDate: formatDateOrDash(dto.transactionDate),
    byUnit,
    unitType: defaultUnitType(byUnit),
    massBalancePeriod: formatTextOrDash(dto.massBalancePeriodLabel),
    reference: formatTextOrDash(dto.reference?.orderNo ?? dto.reference?.shipmentNo),
    associatedDeclarationNo: formatTextOrDash(dto.associatedDeclarationNo),
    transactedBy: formatTextOrDash(dto.transactedBy?.name || dto.transactedBy?.email),
    countryOfOrigin: formatTextOrDash(dto.countryOfOrigin),
    rawMaterialType: formatTextOrDash(dto.rawMaterialType),
    ghgEmissions: dto.ghgEmission == null ? '-' : formatNumber(dto.ghgEmission),
    ghgEmissionSavings: formatPercentOrDash(dto.ghgEmissionSavings),
    feedstockCategory: formatTextOrDash(feedstockCategoryLabel(dto.feedstockCategory)),
    allocatedQuota: formatTextOrDash(dto.allocatedQuota),
    dateOfDispatch: formatDateOrDash(dto.dateOfDispatch),
    daysLeft: dto.daysLeft ?? null,
    canGenerateDeclaration: Boolean(dto.canGenerateDeclaration),
    canRevoke: Boolean(dto.canRevoke),
  };
};

export const processShipmentAllocations = (connection) =>
  (connection?.nodes ?? []).map(processShipmentAllocation);

// Form values → POST /:module/pending-orders/shipments; the server derives the declaration quantity from `shipmentQuantityUnit`.
export const buildOutgoingShipmentDto = (values) => {
  const facility = parseFacilityValue(values.facilityId);
  const isSite = facility.kind === FACILITY_KINDS.site;

  return {
    shipmentNo: [values.idPrefix, values.idNumber].filter(Boolean).join('-') || undefined,
    siteId: isSite ? Number(facility.id) : undefined,
    backToBackAccountId: isSite ? undefined : Number(facility.id),
    materialId: Number(values.materialId),
    customerAccountId: Number(values.soldToId),
    shippingDetailsId: values.shippingToId ? Number(values.shippingToId) : undefined,
    displayUnitId: Number(values.displayUnitId),
    // A back-to-back facility has no terminals, so the field is never sent for one.
    terminalId: isSite && values.terminalId ? Number(values.terminalId) : undefined,
    shipmentQuantity: Number(values.shipmentQuantity),
    shipmentQuantityUnit: values.shipmentQuantityUnit || undefined,
    dispatchDate: values.dateOfDispatch,
    vesselName: values.vesselName || undefined,
  };
};

const DATE_INPUT_FORMAT = 'yyyy-MM-dd';

// `SP-R87ZYNAM` → the prefix box + the number box; a number with no prefix keeps the box empty.
const splitShipmentNo = (shipmentNo) => {
  const separator = (shipmentNo ?? '').indexOf('-');
  if (separator === -1) return { idPrefix: '', idNumber: shipmentNo ?? '' };
  return {
    idPrefix: shipmentNo.slice(0, separator),
    idNumber: shipmentNo.slice(separator + 1),
  };
};

const toText = (value) => (value == null ? '' : String(value));

// What the linked orders already claim, per unit — the floor Edit may not go below.
const buildAssignedByUnit = (dto) => {
  const assigned = {};
  const working = dto.workingUnit?.unitName;
  const declaration = dto.declarationUnit?.unitName;
  if (working != null && dto.totalQuantityWorking != null) {
    assigned[working] = dto.totalQuantityWorking - (dto.unassignedQuantityWorking ?? 0);
  }
  if (declaration != null && dto.totalQuantityDeclaration != null) {
    assigned[declaration] = dto.totalQuantityDeclaration - (dto.unassignedQuantityDeclaration ?? 0);
  }
  return assigned;
};

// GET /:module/pending-orders/shipments/:id → what <OutgoingShipmentForm> needs to prefill Edit.
export const processOutgoingShipmentForm = (dto) => ({
  values: {
    ...splitShipmentNo(dto.shipmentNo),
    facilityId: dto.site
      ? buildFacilityValue(FACILITY_KINDS.site, dto.site.id)
      : dto.backToBackAccount
        ? buildFacilityValue(FACILITY_KINDS.backToBack, dto.backToBackAccount.id)
        : '',
    terminalId: toText(dto.terminalId),
    materialId: toText(dto.material?.id),
    soldToId: toText(dto.customerAccount?.id),
    shippingToId: toText(dto.shippingDetails?.id),
    // Already in whichever unit it was saved in, so it pairs with shipmentQuantityUnit.
    shipmentQuantity: toText(dto.shipmentQuantity),
    shipmentQuantityUnit: dto.shipmentQuantityUnit ?? '',
    displayUnitId: toText(dto.displayUnit?.id),
    dateOfDispatch: formatDate(dto.dispatchDate, DATE_INPUT_FORMAT),
    vesselName: dto.vesselName ?? '',
  },
  // Once anything is allocated, only the shipment quantity stays editable.
  lockedExceptQuantity: (dto.allocatedQuantity ?? 0) > 0,
  assignedByUnit: buildAssignedByUnit(dto),
});
